import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch } from './api';

function formatDateIT(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function ContattiPrenotazioneNuovoPartecipanti({ idIniziativa, onSelectionChange }) {
  const [iscritti, setIscritti] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});

  // Filtri
  const [filterNome, setFilterNome] = useState('');
  const [filterEnte, setFilterEnte] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterAssociato, setFilterAssociato] = useState('');
  const [filterDetermina, setFilterDetermina] = useState('');
  const [filterModalita, setFilterModalita] = useState('');

  // Ordinamento
  const [sortField, setSortField] = useState('nome');
  const [sortAsc, setSortAsc] = useState(true);

  const selectAllRef = useRef();

  const stableOnSelectionChange = useCallback((data) => {
    if (onSelectionChange) onSelectionChange(data);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Carica iscritti quando cambia l'iniziativa
  useEffect(() => {
    if (!idIniziativa) {
      setIscritti([]);
      setLoading(false);
      return;
    }

    async function fetchIscritti() {
      setLoading(true);
      try {
        const response = await apiFetch(`/api/Iniziative/mw-prenotati/${idIniziativa}`);
        if (response.ok) {
          const data = await response.json();
          setIscritti(Array.isArray(data) ? data : []);
        } else {
          if (response.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/';
            return;
          }
          setIscritti([]);
        }
      } catch (err) {
        console.error('[MIDDLEWARE] Errore caricamento iscritti:', err);
        setIscritti([]);
      }
      setLoading(false);
    }

    fetchIscritti();
  }, [idIniziativa]);

  // Reset selezione quando cambia l'iniziativa
  useEffect(() => {
    setSelected({});
    stableOnSelectionChange({ selectedRows: [], destinatariCount: 0, emailCount: 0, placeholders: [] });
  }, [idIniziativa, stableOnSelectionChange]);

  // Filtri
  useEffect(() => {
    let f = Array.isArray(iscritti) ? [...iscritti] : [];

    if (filterNome.trim()) {
      const s = filterNome.toLowerCase();
      f = f.filter(p => (p.nome || '').toLowerCase().includes(s));
    }
    if (filterEnte.trim())
      f = f.filter(p => (p.ente || '').toLowerCase().includes(filterEnte.toLowerCase()));
    if (filterEmail.trim())
      f = f.filter(p => (p.email || '').toLowerCase().includes(filterEmail.toLowerCase()));
    if (filterAssociato === 'SI') f = f.filter(p => p.isAssociato === true);
    if (filterAssociato === 'NO') f = f.filter(p => p.isAssociato === false);
    if (filterDetermina === 'SI') f = f.filter(p => p.determina === true);
    if (filterDetermina === 'NO') f = f.filter(p => p.determina === false);
    if (filterModalita.trim())
      f = f.filter(p => (p.modalita || '').toLowerCase().includes(filterModalita.toLowerCase()));

    if (sortField) {
      f.sort((a, b) => {
        let va = a[sortField] ?? '', vb = b[sortField] ?? '';
        if (typeof va === 'boolean') va = va ? 1 : 0;
        if (typeof vb === 'boolean') vb = vb ? 1 : 0;
        if (typeof va === 'string') va = va.toLowerCase();
        if (typeof vb === 'string') vb = vb.toLowerCase();
        if (va === vb) return 0;
        return (va > vb ? 1 : -1) * (sortAsc ? 1 : -1);
      });
    }

    setFiltered(f);
  }, [iscritti, filterNome, filterEnte, filterEmail, filterAssociato, filterDetermina, filterModalita, sortField, sortAsc]);

  const getRowKey = (p, idx) => `${p.id}-${idx}`;

  const buildCallbackData = useCallback((selMap, rows) => {
    const selRows = rows.filter((p, idx) => selMap[getRowKey(p, idx)]);
    const mappedRows = selRows.map(p => ({
      ...p,
      emailOrd: p.email ? [p.email] : [],
      emailPec: [],
      dataPrenotazione: formatDateIT(p.dataPrenotazione),
      isAssociato: p.isAssociato ? 'Associato' : 'Non Associato',
    }));
    const sample = mappedRows[0] || {};
    const fieldPlaceholders = Object.keys(sample)
      .filter(k => !['emailOrd', 'emailPec', 'id'].includes(k))
      .map(k => `{ISCRITTO_${k.toUpperCase()}}`);
    const genericPlaceholders = ['{DATA_ODIERNA}', '{IMMAGINE_INTESTAZIONE}', '{IMMAGINE_FONDO}', '{FIRMA_ANUTEL}'];
    return {
      selectedRows: mappedRows,
      destinatariCount: mappedRows.length,
      emailCount: mappedRows.reduce((s, p) => s + (p.emailOrd?.length || 0), 0),
      placeholders: Array.from(new Set([...fieldPlaceholders, ...genericPlaceholders])),
    };
  }, []);

  const handleSelect = (rowKey, checked) => {
    if (!idIniziativa) return;
    setSelected(prev => {
      const updated = { ...prev, [rowKey]: checked };
      setTimeout(() => stableOnSelectionChange(buildCallbackData(updated, filtered)), 0);
      return updated;
    });
  };

  const handleSelectAll = (checked) => {
    if (!idIniziativa) return;
    if (checked) {
      const newSel = {};
      filtered.forEach((p, idx) => { newSel[getRowKey(p, idx)] = true; });
      setSelected(newSel);
      setTimeout(() => stableOnSelectionChange(buildCallbackData(newSel, filtered)), 0);
    } else {
      setSelected({});
      stableOnSelectionChange({ selectedRows: [], destinatariCount: 0, emailCount: 0, placeholders: [] });
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortAsc(p => !p);
    else { setSortField(field); setSortAsc(true); }
  };

  const getSortIcon = (field) =>
    sortField === field
      ? (sortAsc ? <i className="fas fa-sort-up ms-1"></i> : <i className="fas fa-sort-down ms-1"></i>)
      : <i className="fas fa-sort text-secondary ms-1"></i>;

  const selectedCount = filtered.filter((p, idx) => selected[getRowKey(p, idx)]).length;
  const totalCount = filtered.length;

  useEffect(() => {
    if (selectAllRef.current)
      selectAllRef.current.indeterminate = selectedCount > 0 && selectedCount < filtered.length;
  }, [selected, filtered, selectedCount]);

  // Raccoglie le modalità distinte per il dropdown
  const modalitaOptions = [...new Set(iscritti.map(p => p.modalita).filter(Boolean))].sort();

  return (
    <div className="card my-4">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-users me-2"></i>1. Seleziona Destinatari
          </h5>
          <div>
            <span className="badge bg-primary me-2">
              <i className="fas fa-list-ol me-1"></i>Totale: {totalCount}
            </span>
            <span className="badge bg-success">Selezionati: {selectedCount}</span>
          </div>
        </div>
      </div>
      <div className="card-body">
        {!idIniziativa ? (
          <div className="text-center text-muted py-5">
            <i className="fas fa-info-circle fa-3x mb-3"></i>
            <h5>Seleziona prima un'iniziativa</h5>
            <p>Clicca su un'iniziativa nell'elenco sopra per visualizzare gli iscritti.</p>
          </div>
        ) : (
          <>
            {/* Filtri */}
            <div className="row mb-2">
              <div className="col-md-4">
                <label className="form-label fw-bold text-start w-100">
                  <i className="fas fa-user me-1 text-muted"></i> Nome:
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Cerca per nome..."
                  value={filterNome}
                  onChange={e => setFilterNome(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold text-start w-100">
                  <i className="fas fa-university me-1 text-muted"></i> Ente:
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Filtra per ente..."
                  value={filterEnte}
                  onChange={e => setFilterEnte(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold text-start w-100">
                  <i className="fas fa-envelope me-1 text-muted"></i> Email:
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Filtra per email..."
                  value={filterEmail}
                  onChange={e => setFilterEmail(e.target.value)}
                />
              </div>
            </div>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label fw-bold text-start w-100">
                  <i className="fas fa-id-card me-1 text-muted"></i> Associato:
                </label>
                <select
                  className="form-select form-select-sm"
                  value={filterAssociato}
                  onChange={e => setFilterAssociato(e.target.value)}
                >
                  <option value="">Tutti</option>
                  <option value="SI">Associati</option>
                  <option value="NO">Non associati</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold text-start w-100">
                  <i className="fas fa-file-alt me-1 text-muted"></i> Determina:
                </label>
                <select
                  className="form-select form-select-sm"
                  value={filterDetermina}
                  onChange={e => setFilterDetermina(e.target.value)}
                >
                  <option value="">Tutti</option>
                  <option value="SI">Con Determina</option>
                  <option value="NO">Senza Determina</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-bold text-start w-100">
                  <i className="fas fa-desktop me-1 text-muted"></i> Modalità:
                </label>
                <select
                  className="form-select form-select-sm"
                  value={filterModalita}
                  onChange={e => setFilterModalita(e.target.value)}
                >
                  <option value="">Tutte</option>
                  {modalitaOptions.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tabella */}
            <div className="table-responsive position-relative" style={{ maxHeight: 400, overflowY: 'auto' }}>
              {loading && (
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white bg-opacity-75" style={{ zIndex: 10 }}>
                  <i className="fas fa-spinner fa-spin fa-3x text-primary mb-3"></i>
                  <div className="fw-bold fs-5">Caricamento iscritti...</div>
                </div>
              )}
              <table className="table table-striped table-hover mb-0">
                <thead className="table-dark sticky-top">
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        ref={selectAllRef}
                        checked={selectedCount === filtered.length && filtered.length > 0}
                        onChange={e => handleSelectAll(e.target.checked)}
                      />
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('nome')}>
                      Nome {getSortIcon('nome')}
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('ente')}>
                      Ente {getSortIcon('ente')}
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('modalita')}>
                      Modalità {getSortIcon('modalita')}
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('isAssociato')}>
                      Associato {getSortIcon('isAssociato')}
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('determina')}>
                      Determina {getSortIcon('determina')}
                    </th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>
                      Email {getSortIcon('email')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length > 0 ? filtered.map((p, idx) => {
                    const rowKey = getRowKey(p, idx);
                    return (
                      <tr key={rowKey}>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!selected[rowKey]}
                            onChange={e => handleSelect(rowKey, e.target.checked)}
                          />
                        </td>
                        <td className="fw-semibold">{p.nome}</td>
                        <td>
                          <div className="text-truncate" style={{ maxWidth: 200 }} title={p.ente}>
                            {p.ente}
                          </div>
                        </td>
                        <td>
                          {p.modalita && (
                            <span className={`badge ${p.modalita === 'In Presenza' ? 'bg-primary' : 'bg-warning text-dark'}`}>
                              {p.modalita}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${p.isAssociato ? 'bg-success' : 'bg-secondary'}`}>
                            {p.isAssociato ? 'SI' : 'NO'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${p.determina ? 'bg-success' : 'bg-secondary'}`}>
                            {p.determina ? 'SI' : 'NO'}
                          </span>
                        </td>
                        <td>
                          <small>{p.email}</small>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        <i className="fas fa-address-book fa-2x mb-2"></i><br />
                        {loading ? 'Caricamento in corso...' : 'Nessun iscritto trovato'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filtered.length > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-2">
                <small className="text-muted">
                  <i className="fas fa-info-circle me-1"></i>
                  {filtered.length} di {iscritti.length} iscritti
                </small>
                {selectedCount > 0 && (
                  <small className="text-success">
                    <i className="fas fa-envelope me-1"></i>
                    {selectedCount} destinatari selezionati
                  </small>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ContattiPrenotazioneNuovoPartecipanti;
